
/**
 * Healthcare Search Composable for HYS v5 Platform
 * 
 * Provides comprehensive search functionality for healthcare providers with:
 * - SSR/SSG-safe implementation for better SEO
 * - Robust error handling and user feedback
 * - Performance optimization with debouncing and caching
 * - TypeScript compliance with strict typing
 * 
 * @fileoverview Advanced search with healthcare-specific optimizations
 * @version 2.0.0
 * @author HYS Development Team
 */

import type { Ref } from 'vue'

// Search-related type definitions for better TypeScript compliance
export interface SearchResult {
  readonly id: string
  readonly name: string
  readonly specialty: string
  readonly address?: string
  readonly phone?: string
  readonly npi?: string
  readonly image?: string
}

export interface SearchError extends Error {
  code: string
  statusCode?: number | undefined
  userMessage: string
  developerMessage: string
  details?: Record<string, unknown> | undefined
}

export interface SearchFilters {
  readonly specialty?: string
  readonly location?: string
  readonly insurance?: string
  readonly language?: string
}

/**
 * Advanced healthcare search composable with comprehensive error handling
 * Optimized for SSR/SSG and LLM accessibility
 * 
 * @returns Search methods and reactive state
 */
const useSearch = () => {
    // Toast functionality - fallback if useGlobalToast not available
    const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
        try {
            // Try to use global toast if available
            const globalToast = (globalThis as any)?.useGlobalToast?.()
            if (globalToast?.showToast) {
                globalToast.showToast(message, type)
            } else {
                // Fallback to console logging for development
                console.log(`[Toast ${type.toUpperCase()}]: ${message}`)
            }
        } catch (error) {
            console.log(`[Toast ${type.toUpperCase()}]: ${message}`)
        }
    }

    // Reactive search state with proper typing
    const searchQuery: Ref<string> = useState("searchQuery", () => "")
    const searchResults: Ref<SearchResult[]> = useState("searchResults", () => [])
    const listFocusIndex: Ref<number> = useState("listFocusIndex", () => -1)
    const currentPage: Ref<number> = useState("currentPage", () => 1)
    
    // Internal state management
    const retry_counter = ref(0)
    const isLoading = ref(false)
    const lastSearchTime = ref(0)
    const searchAbortController = ref<AbortController | null>(null)

    // Private specialty filter with reactive updates
    const _filteringSpecialty = ref("")

    /**
     * Creates a standardized search error with user-friendly messaging
     */
    const createSearchError = (
        code: string,
        message: string,
        userMessage: string,
        statusCode?: number,
        details?: Record<string, unknown>
    ): SearchError => {
        const error = new Error(message) as SearchError
        error.code = code
        error.statusCode = statusCode
        error.userMessage = userMessage
        error.developerMessage = message
        error.details = details
        return error
    }

    /**
     * Reactive specialty filter with automatic search triggering
     * Implements proper SSR/SSG safety checks
     */
    const filteringSpecialty = computed({
        get: () => _filteringSpecialty.value,
        set: async (value: string) => {
            try {
                _filteringSpecialty.value = value
                
                // Only trigger search on client-side for better SSR/SSG compatibility
                if (process.client && searchQuery.value.trim()) {
                    await getSearchResults(searchQuery.value)
                }
            } catch (error) {
                console.error('[useSearch] Filter update failed:', error)
                showToast('搜索筛选器更新失败，请重试', 'error')
            }
        }
    })

    /**
     * Main search function with comprehensive error handling and performance optimization
     * 
     * @param query - Search query string, array of strings, or undefined (will be normalized)
     * @param size - Results page size (optional)
     * @returns Promise<SearchResult[]> - Array of search results
     */
    const getSearchResults = async (query: string | string[] | undefined, size?: string): Promise<SearchResult[]> => {
        // Normalize query parameter to handle route.query values
        const normalizedQuery = Array.isArray(query) 
            ? query[0] || '' 
            : typeof query === 'string' 
                ? query 
                : ''
        // Performance optimization: prevent duplicate concurrent requests
        if (isLoading.value) {
            console.debug('[useSearch] Request already in progress, returning cached results')
            return searchResults.value
        }

        // Circuit breaker: prevent excessive retries without valid query
        if (!normalizedQuery.trim() && retry_counter.value > 3) {
            console.debug('[useSearch] Skipping search - no query and retry limit exceeded')
            showToast('请输入搜索关键词', 'warning')
            return []
        }

        // Rate limiting: prevent too frequent requests
        const now = Date.now()
        if (now - lastSearchTime.value < 300) { // 300ms debounce
            console.debug('[useSearch] Rate limiting applied')
            return searchResults.value
        }

        isLoading.value = true
        lastSearchTime.value = now
        
        // Cancel previous request if still pending
        if (searchAbortController.value) {
            searchAbortController.value.abort()
        }
        searchAbortController.value = new AbortController()
        
        try {
            console.debug('[useSearch] Executing search:', { query: normalizedQuery, specialty: filteringSpecialty.value })
            
            const queryResult = await $fetch('/api/doctor/search', {
                method: 'GET',
                signal: searchAbortController.value.signal,
                query: {
                    q: normalizedQuery.trim() || '',
                    specialty: filteringSpecialty.value,
                    size: size || "8"
                },
                timeout: 10000 // 10 second timeout
            })
            
            console.debug('[useSearch] Search completed successfully:', { 
                resultCount: queryResult?.length || 0,
                query: normalizedQuery,
                specialty: filteringSpecialty.value 
            })
            
            // Validate and sanitize results
            const validResults = Array.isArray(queryResult) ? queryResult : []
            searchResults.value = validResults
            retry_counter.value = 0 // Reset retry counter on success
            
            // User feedback for empty results
            if (validResults.length === 0 && normalizedQuery.trim()) {
                showToast('未找到相关医生，请尝试其他搜索条件', 'info')
            }
            
            return validResults
            
        } catch (error: any) {
            const isAborted = error.name === 'AbortError'
            
            if (!isAborted) {
                console.error('[useSearch] Search request failed:', error)
                retry_counter.value++
                
                // Enhanced error handling with user feedback
                createSearchError(
                    'SEARCH_API_ERROR',
                    `Search API request failed: ${error.message}`,
                    '搜索服务暂时不可用，请稍后重试',
                    error.statusCode,
                    { query: normalizedQuery, specialty: filteringSpecialty.value, attempt: retry_counter.value }
                )
                
                // Smart retry logic with exponential backoff
                if (retry_counter.value < 3 && normalizedQuery.trim()) {
                    const backoffDelay = Math.min(1000 * Math.pow(2, retry_counter.value - 1), 5000)
                    console.debug(`[useSearch] Retrying search in ${backoffDelay}ms (attempt ${retry_counter.value}/3)`)
                    
                    await new Promise(resolve => setTimeout(resolve, backoffDelay))
                    return await getSearchResults(query, size)
                } else {
                    // Final failure - provide helpful user guidance
                    console.warn('[useSearch] Search failed after exhausting retries')
                    showToast('搜索失败，请检查网络连接或稍后重试', 'error')
                    searchResults.value = []
                    return []
                }
            } else {
                console.debug('[useSearch] Search request was cancelled')
                return searchResults.value
            }
        } finally {
            isLoading.value = false
            searchAbortController.value = null
        }
    }

    /**
     * Get search suggestions for autocomplete
     * Optimized for fast response and minimal data transfer
     */
    const getSearchSuggestions = async (query: string): Promise<any[]> => {
        if (!query.trim() || query.length < 2) {
            return []
        }

        try {
            console.debug('[useSearch] Fetching suggestions for:', query)
            
            const suggestions = await $fetch('/api/doctor/suggestions', {
                query: { q: query.trim() },
                timeout: 3000
            })
            
            console.debug('[useSearch] Suggestions fetched:', suggestions?.length || 0)
            return Array.isArray(suggestions) ? suggestions : []
            
        } catch (error: any) {
            console.error('[useSearch] Suggestions request failed:', error)
            return []
        }
    }

    /**
     * Loads additional search results for pagination
     * Implements proper error handling and state management
     */
    const loadMoreResults = async (): Promise<SearchResult[]> => {
        // Prevent concurrent load more requests
        if (isLoading.value) {
            console.debug('[useSearch] Load more already in progress')
            return []
        }

        const query = searchQuery.value?.trim()
        if (!query) {
            console.debug('[useSearch] No query available for load more')
            return []
        }

        console.debug('[useSearch] Loading more results:', { 
            query, 
            nextPage: currentPage.value + 1,
            specialty: filteringSpecialty.value 
        })
        
        isLoading.value = true
        
        try {
            const queryResult = await $fetch('/api/doctor/search', {
                method: 'GET',
                query: {
                    q: query,
                    page: currentPage.value + 1,
                    specialty: filteringSpecialty.value,
                    size: "8"
                },
                timeout: 10000
            })
            
            console.debug(`[useSearch] Load more page ${currentPage.value + 1} completed:`, {
                newResults: queryResult?.length || 0
            })
            
            // Validate and append new results
            const validResults = Array.isArray(queryResult) ? queryResult : []
            
            if (validResults.length > 0) {
                searchResults.value = [...searchResults.value, ...validResults]
                currentPage.value++
                
                showToast(`加载了 ${validResults.length} 个新结果`, 'success')
            } else {
                showToast('没有更多结果了', 'info')
            }
            
            return validResults
            
        } catch (error: any) {
            console.error('[useSearch] Load more results failed:', error)
            
            const loadMoreError = createSearchError(
                'LOAD_MORE_ERROR',
                `Load more request failed: ${error.message}`,
                '加载更多结果失败，请重试',
                error.statusCode,
                { query, page: currentPage.value + 1 }
            )
            
            showToast(loadMoreError.userMessage, 'error')
            return []
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Resets search state to initial values
     * Useful for clearing search results and starting fresh
     */
    const resetSearch = (): void => {
        try {
            // Cancel any pending requests
            if (searchAbortController.value) {
                searchAbortController.value.abort()
                searchAbortController.value = null
            }
            
            // Reset all state
            searchQuery.value = ""
            searchResults.value = []
            listFocusIndex.value = -1
            currentPage.value = 1
            retry_counter.value = 0
            isLoading.value = false
            lastSearchTime.value = 0
            _filteringSpecialty.value = ""
            
            console.debug('[useSearch] Search state reset successfully')
        } catch (error) {
            console.error('[useSearch] Failed to reset search state:', error)
        }
    }

    // Enhanced reactive state for doctor details
    const targetDoctorSpecialty: Ref<string> = useState("targetDoctorSpecialty", () => "")
    const targetDoctorName: Ref<string> = useState("targetDoctorName", () => "")
    const doctorDetails: Ref<Record<string, any>> = useState("doctorDetails", () => ({}))

    // Computed properties for better UX
    const hasResults = computed(() => searchResults.value.length > 0)
    const isSearching = computed(() => isLoading.value)
    const canLoadMore = computed(() => !isLoading.value && hasResults.value)

    // SEO and LLM-friendly search state
    const searchMetadata = computed(() => ({
        query: searchQuery.value,
        resultCount: searchResults.value.length,
        specialty: filteringSpecialty.value,
        hasMorePages: currentPage.value > 1,
        isLoading: isLoading.value,
        timestamp: new Date().toISOString()
    }))

    /**
     * Public API with comprehensive functionality
     * Designed for optimal developer experience and maintainability
     */
    return {
        // Core search functionality
        searchQuery, 
        searchResults, 
        getSearchResults, 
        getSearchSuggestions,
        loadMoreResults,
        resetSearch,
        
        // UI state management
        listFocusIndex,
        isLoading,
        filteringSpecialty,
        
        // Doctor details
        doctorDetails, 
        targetDoctorSpecialty,
        targetDoctorName,
        
        // Computed helpers
        hasResults,
        isSearching,
        canLoadMore,
        searchMetadata,
        
        // Error handling
        createSearchError,
        
        // Types (for external use)
        // TypeScript interfaces are exported at module level
    } as const
}

export default useSearch